// R1b-α Step 2 — Owner Command Center authoritative wiring.
// Renders every tile the Section 11.5 directive calls for, grouped into
// four honest sections (Now · Money · Compliance & Ops · Intelligence).
// Every tile is either a real tenant-scoped query (with source + "as of") or a
// register-derived truthful state — zero fabricated counts.
import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { AdminAppShell } from "../design-system/shells/AdminAppShell";
import OwnerFirstRunTour from "../tours/OwnerFirstRunTour";
import { useAuth } from "../lib/auth";
import StatusChip from "../components/StatusChip";
import { api } from "../lib/api";
import { Card, CardContent } from "../components/ui/card";
import {
  DropdownMenu, DropdownMenuTrigger, DropdownMenuContent,
  DropdownMenuItem, DropdownMenuLabel, DropdownMenuSeparator,
} from "../components/ui/dropdown-menu";
import { toast } from "sonner";
import { ChevronRight, ArrowUpRight, UserCog } from "lucide-react";
import NewThisHourTile from "../components/NewThisHourTile";
// Release C-1 — Open Microsoft 365 Mail action for Owner + Administrator.
import OpenInMicrosoftMailButton from "../design-system/microsoft365/OpenInMicrosoftMailButton";

// B2a (Finding 001): the backend decides each tile's state, destination (`to`), data source and freshness. The
// frontend never invents a phase, a status or a link.
function formatValue(tile) {
  if (tile.status !== "active") return "—";
  const v = tile.value;
  if (v === null || v === undefined) return "—";
  if (tile.unit === "usd" && typeof v === "number") return "$" + v.toLocaleString();
  if (tile.unit === "percent" && typeof v === "number") return v + "%";
  if (typeof v === "object") return Object.entries(v).map(([k, n]) => `${n} ${k}`).join(" · ");
  return String(v);
}

function asOf(iso) {
  if (!iso) return null;
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? null : d.toLocaleString([], { dateStyle: "medium", timeStyle: "short" });
}

function TileBody({ tile }) {
  const fresh = asOf(tile.as_of);
  const isActive = tile.status === "active";
  return (
    <CardContent className="p-4 flex flex-col justify-between h-full min-h-[140px] min-w-0">
      <div className="flex flex-wrap items-start justify-between gap-2">
        <h3 className="text-[11px] font-nav uppercase tracking-wider text-erf-dark/70 min-w-0 break-words">{tile.label}</h3>
        {!isActive && <StatusChip status={tile.status} testId={`owner-tile-${tile.key}-state`} />}
      </div>
      <div className="mt-3 min-w-0">
        <div className="font-mono text-3xl font-semibold text-erf-dark leading-none break-words">{formatValue(tile)}</div>
        {tile.expired ? <div className="text-[11px] mt-1 text-red-700 font-mono">{tile.expired} expired</div> : null}
        {tile.subtitle && <div className="text-[11px] mt-2 font-nav text-erf-dark/70">{tile.subtitle}</div>}
        {tile.latest_check_at && (
          <div data-testid={`owner-tile-${tile.key}-latest-check`} className="text-[11px] mt-1 font-nav text-erf-dark/70">
            Latest recorded check: {asOf(tile.latest_check_at)}
          </div>
        )}
        {tile.note && (
          <div data-testid={`owner-tile-${tile.key}-truthful-note`} className="text-[11px] mt-1 font-nav text-erf-dark/70">
            {tile.note}
          </div>
        )}
        {isActive && tile.source && (
          <div data-testid={`owner-tile-${tile.key}-source`} className="text-[10px] mt-2 font-mono text-erf-dark/60">
            Source: {tile.source}{fresh ? ` · as of ${fresh}` : ""}
          </div>
        )}
      </div>
    </CardContent>
  );
}

function Tile({ tile }) {
  const card = (
    <Card className={`border-erf-borderCream bg-erf-cream h-full transition-all ${tile.to ? "hover:border-erf-gold/60 hover:shadow-md" : ""}`}
          data-testid={`owner-tile-${tile.key}`} data-state={tile.status}>
      <TileBody tile={tile} />
    </Card>
  );
  return tile.to ? (
    <Link to={tile.to} data-testid={`owner-tile-link-${tile.key}`}
          className="block h-full rounded-md focus-visible:outline focus-visible:outline-2 focus-visible:outline-erf-gold">
      {card}
    </Link>
  ) : <div className="h-full">{card}</div>;
}

function Section({ section }) {
  return (
    <section className="mt-8 first:mt-0" data-testid={`owner-section-${section.key}`}>
      <div className="flex items-baseline gap-3 mb-3">
        <div className="text-[10px] font-mono uppercase tracking-[0.28em] text-erf-gold">
          {section.label}
        </div>
        <div className="flex-1 h-px bg-erf-borderCream" aria-hidden="true" />
      </div>
      <div
        className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4"
        data-testid={`owner-tiles-grid-${section.key}`}
      >
        {section.tiles.map((t) => <Tile key={t.key} tile={t} />)}
      </div>
    </section>
  );
}

export default function OwnerCommandCenter() {
  const [data, setData] = useState(null);
  const [err, setErr] = useState("");
  const [inboxCounts, setInboxCounts] = useState(null);
  const { user, startImpersonation } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    api.get("/owner/dashboard").then((r) => setData(r.data)).catch((e) => setErr(e.message));
    // B0-Closeout: Owner Inbox counts tile.
    api.get("/owner/inbox/counts").then((r) => setInboxCounts(r.data)).catch(() => setInboxCounts({ error: true }));
  }, []);

  const LIVE_IMPERSONATION_ROLES = [
    { key: "OWNER_SUPER_ADMIN", label: "Owner (self)" },
    { key: "ADMINISTRATOR",     label: "Administrator" },
    { key: "PROJECT_MANAGER",   label: "Project Manager" },
    { key: "SUPERINTENDENT",    label: "Superintendent" },
    { key: "ESTIMATOR",         label: "Estimator" },
    { key: "CLIENT",            label: "Client" },
  ];

  const handleImpersonate = async (roleKey) => {
    try {
      await startImpersonation(`role:${roleKey}`);
      toast.success(`Impersonating as ${roleKey.replaceAll("_", " ")}`);
      navigate(roleKey === "CLIENT" ? "/client/portal" : "/owner");
    } catch (e) {
      toast.error("Impersonation failed — opening picker instead.");
      navigate("/admin/portal-switch");
    }
  };

  // Legacy compatibility — retain the old flat `owner-tiles-grid` testid on
  // the outermost wrapper so any existing scripts still resolve at least
  // one grid target.
  return (
    <AdminAppShell
      activeNav="command"
      title="Owner Command Center"
      subtitle={data?.tenant_name ? `${data.tenant_name} · Owner executive view` : "Owner executive view"}
      user={user}
    >
      <OwnerFirstRunTour userId={user?.id} />
      <div className="mx-auto w-full max-w-[1600px] box-border px-4 md:px-6 lg:px-8">
        {/* Release C-1 · Open in Microsoft 365 Mail — Owner + Administrator only. */}
        <div className="mb-4 flex justify-end" data-testid="owner-command-mail-strip">
          <OpenInMicrosoftMailButton surface="owner_command_center" size="pill" />
        </div>
        {err && <div className="text-red-700 text-sm mb-4">{err}</div>}
        {!data ? (
          <div className="font-mono text-xs text-erf-dark/60">Loading dashboard…</div>
        ) : (
          <>
            <div data-testid="owner-tiles-grid" aria-label="Owner tiles">
              {(data.sections || []).map((s) => <Section key={s.key} section={s} />)}
            </div>

            {/* B0-Closeout — Owner Requests Inbox tile. Persistence-first
                counts drawn from /api/owner/inbox/counts. Sub-indicator
                surfaces delivery failures + deferrals. */}
            <section className="mt-8" data-testid="owner-section-inbox">
              <div className="flex items-baseline gap-3 mb-3">
                <div className="text-[10px] font-mono uppercase tracking-[0.28em] text-erf-gold">Owner Requests Inbox</div>
                <div className="flex-1 h-px bg-erf-borderCream" aria-hidden="true" />
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
                {[
                  { key: "new_requests", label: "New", filter: "workflow_state=new" },
                  { key: "awaiting_owner_review", label: "Awaiting Owner", filter: "workflow_state=awaiting_owner" },
                  { key: "questionnaires_awaiting_review", label: "Questionnaires", filter: "kind=applicant_questionnaire" },
                  { key: "failed_notifications", label: "Delivery failures", filter: "kind=delivery_failure" },
                  { key: "needing_more_info", label: "Needs info", filter: "workflow_state=info_requested" },
                ].map((c) => (
                  <Link to={`/admin/owner/inbox?${c.filter}`} key={c.key}
                        data-testid={`owner-inbox-tile-${c.key}`}
                        className="block bg-white border border-erf-borderCream rounded-md p-4 hover:shadow transition">
                    <div className="text-[10px] font-mono uppercase tracking-widest text-erf-dark/60">{c.label}</div>
                    <div className={"text-3xl font-display font-semibold mt-1 " + (c.key === "failed_notifications" && (inboxCounts?.[c.key] || 0) > 0 ? "text-red-700" : "text-erf-dark")}>
                      {inboxCounts && !inboxCounts.error ? (inboxCounts[c.key] ?? 0) : "—"}
                    </div>
                    <div className="text-[10px] font-mono text-erf-dark/60 mt-2" data-testid={`owner-inbox-tile-${c.key}-source`}>
                      {!inboxCounts ? "Loading…" : inboxCounts.error ? "Count unavailable right now"
                        : `Source: ${inboxCounts.source} · as of ${asOf(inboxCounts.as_of)}`}
                    </div>
                    <div className="text-[10px] text-erf-dark/50 mt-1">Open the Inbox →</div>
                  </Link>
                ))}
              </div>
              <div className="mt-2 text-[11px] text-erf-dark/60">
                Every public/business submission arriving at ConstructionOS.
                Delivery chip legend: <b>provider_accepted</b> is HTTP 202
                (not delivery); <b>delivered</b> is set only by a signed
                webhook event.
              </div>
            </section>

            <NewThisHourTile />

            <section className="mt-8">
              <div className="flex items-center justify-between mb-3">
                <h2 className="font-display font-semibold text-lg text-erf-dark">Recent Audit Activity</h2>
                <Link to="/admin/audit-trust/ledger" className="text-xs font-nav text-erf-gold flex items-center gap-1 min-h-[44px] hover:underline" data-testid="owner-audit-all-link">
                  Open Audit Ledger <ChevronRight className="w-3.5 h-3.5" />
                </Link>
              </div>
              <div className="bg-white border border-erf-borderCream rounded-md overflow-x-auto" data-testid="owner-audit-table-wrap">
                <table className="w-full erf-table erf-stack-sm" data-testid="owner-audit-table">
                  <thead className="bg-erf-creamSecondary">
                    <tr>
                      <th className="text-left px-3 py-2 mono">#</th>
                      <th className="text-left px-3 py-2">Timestamp (UTC)</th>
                      <th className="text-left px-3 py-2">Actor</th>
                      <th className="text-left px-3 py-2">Action</th>
                      <th className="text-left px-3 py-2">Resource</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(data.recent_audit || []).length === 0 && (
                      <tr><td colSpan={5} className="px-3 py-8 text-center text-erf-dark/50 font-mono text-xs">No activity yet.</td></tr>
                    )}
                    {(data.recent_audit || []).map((e) => (
                      <tr key={e.id} className="border-t border-erf-borderCream/60 hover:bg-erf-creamSecondary/60" data-testid={`owner-audit-row-${e.hash_chain_index}`}>
                        <td data-label="#" className="px-3 py-2 mono text-erf-dark/70">{e.hash_chain_index}</td>
                        <td data-label="Timestamp (UTC)" className="px-3 py-2 mono">{(e.ts || "").replace("T", " ").slice(0, 19)}</td>
                        <td data-label="Actor" className="px-3 py-2 mono text-erf-dark/80">{e.actor_role}</td>
                        <td data-label="Action" className="px-3 py-2 font-nav break-words">{e.action}</td>
                        <td data-label="Resource" className="px-3 py-2 mono text-erf-dark/70 break-all">{e.resource_type}{e.resource_id ? "/" + String(e.resource_id).slice(0, 8) : ""}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </section>

            <section className="mt-8 grid gap-4 md:grid-cols-2 lg:grid-cols-3" data-testid="owner-quick-actions">
              {[
                { to: "/crm/leads", title: "New Lead", sub: "Capture a client inquiry", tag: "CRM" },
                { to: "/admin/documents", title: "Upload Document", sub: "Contracts, permits, plans", tag: "Vault" },
              ].map((q) => (
                <Link key={q.to} to={q.to} data-testid={`owner-quick-${q.to.replace(/\//g,"-")}`} className="bg-white border border-erf-borderCream rounded-md p-4 hover:border-erf-gold/60 hover:shadow-md transition-all flex items-start justify-between gap-3 group">
                  <div>
                    <div className="text-[10px] font-mono uppercase tracking-widest text-erf-gold">{q.tag}</div>
                    <div className="font-display font-semibold text-erf-dark mt-1">{q.title}</div>
                    <div className="text-xs font-nav text-erf-dark/60 mt-0.5">{q.sub}</div>
                  </div>
                  <ArrowUpRight className="w-4 h-4 text-erf-dark/30 group-hover:text-erf-gold transition-colors" />
                </Link>
              ))}
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <button
                    type="button"
                    data-testid="owner-quick-view-as-role"
                    aria-haspopup="menu"
                    aria-label="View as role — one-click impersonation"
                    className="bg-white border border-erf-borderCream rounded-md p-4 hover:border-erf-gold/60 hover:shadow-md transition-all flex items-start justify-between gap-3 group w-full text-left"
                  >
                    <div>
                      <div className="text-[10px] font-mono uppercase tracking-widest text-erf-gold">Owner only</div>
                      <div className="font-display font-semibold text-erf-dark mt-1">View as Role</div>
                      <div className="text-xs font-nav text-erf-dark/60 mt-0.5">One-click immersive impersonation</div>
                    </div>
                    <UserCog className="w-4 h-4 text-erf-dark/30 group-hover:text-erf-gold transition-colors" />
                  </button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" data-testid="owner-quick-view-as-role-menu" aria-live="polite" className="min-w-[220px]">
                  <DropdownMenuLabel className="text-[10px] font-mono uppercase tracking-widest text-erf-dark/60">
                    Live roles
                  </DropdownMenuLabel>
                  {LIVE_IMPERSONATION_ROLES.map((r) => (
                    <DropdownMenuItem
                      key={r.key}
                      data-testid={`owner-quick-role-${r.key}`}
                      onSelect={() => handleImpersonate(r.key)}
                      className="cursor-pointer"
                    >
                      <UserCog className="w-3.5 h-3.5" aria-hidden="true" />
                      <span className="flex-1">{r.label}</span>
                      <ChevronRight className="w-3 h-3 opacity-40" aria-hidden="true" />
                    </DropdownMenuItem>
                  ))}
                  <DropdownMenuSeparator />
                  <DropdownMenuItem
                    data-testid="owner-quick-view-as-role-more"
                    onSelect={() => navigate("/admin/portal-switch")}
                    className="cursor-pointer"
                  >
                    <span className="flex-1 text-erf-dark/70 font-nav text-xs">More roles + per-user targeting…</span>
                    <ChevronRight className="w-3 h-3 opacity-40" aria-hidden="true" />
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </section>
          </>
        )}
      </div>
    </AdminAppShell>
  );
}
